import { Accordion, ActionIcon, Anchor, Box, Button, CopyButton, Divider, Group, Paper, Stack, Text, Textarea, Title, Tooltip } from "@mantine/core"
import { IconCopy, IconDeviceFloppy, IconExternalLink, IconUpload } from "@tabler/icons-react"
import { useAdBuilder } from "./adbuilder-context";
import { useStudioFormContext } from "./form-context";
import { modals } from "@mantine/modals";
import {
    preview_eye,
    analytics,
    help_circle,
    arrow_right,
    google_ad_manager,
    google_display,
    other_ad,
    preview_mock,
    dashboard_mock,
} from "@/assets/img";
import { Link } from "@inertiajs/react";
import { confettiEmitter } from "@/Utilities/confettiEmitter";

import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

import classes from "./FormActionButtons.module.css";

export const FormActionButtons = () => {

    const { t } = useTranslation();
    const { query, mutation } = useAdBuilder();
    const form = useStudioFormContext();
   

    const publishTag = () =>
        mutation.mutate( 
            { ...form.getValues(), status: 4 },
            {
                onSuccess: (response) => {
                    // AD-187: a view-only ad was not saved, so nothing was published.
                    if (response?.viewOnly) return;

                    query.refetch().then(() => { PublishModal({ query }) });

                    confettiEmitter.emit();
                },
                onError: (e) => console.error("Publish error:", e),
            }
        );



    return (
        <Group my="md" justify="center">
            <Button
                variant="filled"
                size="sm"
                color="smartico-blue"
                radius="md"
                leftSection={<IconDeviceFloppy />}
                onClick={form.saveAndPreview}
                loading={mutation.isPending}
            >
                {t('adBuilder.saveAndPreview')}
            </Button>
            <Button variant="filled" size="sm" color="pink" radius="md" leftSection={<IconUpload />} loading={mutation.isPending} onClick={publishTag}>
                {t('adBuilder.publishAndGetTag')}
            </Button>
        </Group>
    )
}


export const PublishModal = ({ query, withFooter = true }) => {
    const adPreviewUrl = query?.data?.ad_preview ?? "#";
    const adDashboardUrl = query?.data?.ad_dashboard ?? "#";
    const CenteredImage = ({ src, alt = "", copyValue }) => (
        <Paper
            withBorder
            style={{
                height: 130,
                borderWidth: 1,
                borderRadius: 6,
                overflow: "hidden",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff",
            }}
        >
            <Box
                style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <img
                    src={src}
                    alt={alt}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                    }}
                />
            </Box>

            {copyValue ? (
                <CopyButton value={copyValue}>
                    {({ copied, copy }) => (
                        <Tooltip label={copied ? i18n.t('common.copied') : i18n.t('common.copy')} withArrow>
                            <Box
                                onClick={copy}
                                role="button"
                                aria-label={i18n.t('common.copy')}
                                style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    width: 34,
                                    height: 34,
                                    border: "1px solid var(--mantine-color-default-border)",
                                    background: "var(--mantine-color-body)",
                                    borderRadius: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    zIndex: 2,
                                }}
                            >
                                <IconCopy size={18} className={classes.gray_color_text} />
                            </Box>
                        </Tooltip>
                    )}
                </CopyButton>
            ) : null}
        </Paper>
    );

    return modals.open({
        modalId: "publish-modal",
        radius: "lg",
        centered: true,
        withCloseButton: true,
        styles: {
            content: { minWidth: 700, padding: 24, paddingTop: 32, overflow: "hidden" },
            header: { justifyContent: "center", padding: 0, marginBottom: 36 },
            close: { position: "absolute", top: -8, right: 0, width: 24, height: 24 },
            body: { padding: 0, overflow: "visible" },
        },
        title: (
            <Stack gap={4} align="center">
                <Title
                    order={4}
                    style={{
                        fontFamily: "Manrope",
                        fontWeight: 600,
                        fontSize: 20,
                        lineHeight: "140%",
                        textAlign: "center",
                    }}
                    className={classes.gray_color_title}
                >
                    {i18n.t('publish.congrats')}
                </Title>
                <Text
                    style={{
                        fontFamily: "Manrope",
                        fontWeight: 400,
                        fontSize: 16,
                        lineHeight: "100%",
                        textAlign: "center",
                    }}
                    className={classes.gray_color_text}
                >
                    {i18n.t('publish.subtitle')}
                </Text>
            </Stack>
        ),
        children: (
            <Stack>
                <Group justify="space-between" align="flex-start" gap={11}>
                                        
               <Anchor
                  href={adPreviewUrl} 
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block", textDecoration: "none", color: "inherit" }}
                >
                 <Box style={{ flex: 1, cursor: adPreviewUrl !== "#" ? "pointer" : "default" }}>
                        <Title
                            order={5}
                            style={{
                                fontWeight: 500,
                                fontSize: 16,
                                lineHeight: "22px",
                                letterSpacing: 0,
                                marginBottom: 11,
                            }}
                        >
                            <Group gap={6} align="center"  className={classes.gray_color_text}>
                                <img src={preview_eye} alt="Preview Icon" width={20} height={20} />
                                {i18n.t('publish.previewAds')}
                                <Tooltip label={i18n.t('publish.previewTooltip')} withArrow>
                                    <img src={help_circle} alt="Help Icon" width={16} height={16} />
                                </Tooltip>
                                <ActionIcon
                                    component="a"
                                    href={query?.data?.ad_preview}
                                    target="_blank"
                                    variant="subtle"
                                    style={{ marginLeft: "auto" }}
                                >
                                    <img src={arrow_right} alt="Arrow Icon" width={20} height={20} />
                                </ActionIcon>
                            </Group>
                        </Title>

                        {/* visual mock instead of iframe */}
                        <CenteredImage src={preview_mock} alt="Preview mock" copyValue={query?.data?.ad_preview} />
                    </Box>
                </Anchor>
                <Anchor
                   href={adDashboardUrl}
                   target="_blank"
                   rel="noopener noreferrer"
                   style={{ display: "block", textDecoration: "none", color: "inherit" }}
                >
                 
                  <Box style={{ flex: 1, cursor: adDashboardUrl !== "#" ? "pointer" : "default" }}>
                        <Title
                            order={5}
                            style={{
                                fontWeight: 500,
                                fontSize: 16,
                                lineHeight: "22px",
                                letterSpacing: 0,
                                marginBottom: 11,
                            }}
                        >
                            <Group gap={6} align="center" className={classes.gray_color_text}>
                                <img src={analytics} alt="Analytics Icon" width={20} height={20} />
                                {i18n.t('publish.dashboard')}
                                <Tooltip label={i18n.t('publish.dashboardTooltip')} withArrow>
                                    <img src={help_circle} alt="Help Icon" width={16} height={16} />
                                </Tooltip>
                                <ActionIcon
                                    component="a"
                                    href={query?.data?.ad_dashboard}
                                    target="_blank"
                                    variant="subtle"
                                    style={{ marginLeft: "auto" }}
                                >
                                    <img src={arrow_right} alt="Arrow Icon" width={20} height={20} />
                                </ActionIcon>
                            </Group>
                        </Title>

                        <CenteredImage src={dashboard_mock} alt="Dashboard mock" copyValue={query?.data?.ad_dashboard} />
                    </Box>
                  </Anchor>
                </Group>

                <Divider size={1} color="#EDEDED" my={20} mx={-24} />

                <Accordion defaultValue={null} multiple={false} variant="filled">
                    <Text
                        style={{
                            fontWeight: 500,
                            fontSize: 18,
                            lineHeight: "100%",
                            marginBottom: 20,
                        }}
                        className={classes.gray_color_text}
                    >
                        {i18n.t('publish.runYourAd')}
                    </Text>

                    {/* GAM with click macro */}
                    <Accordion.Item value="gam" style={{ backgroundColor: "transparent" }}>
                        <Accordion.Control
                            styles={{ control: { padding: 0 }, label: { overflow: "visible" } }}
                            style={{ padding: 0, backgroundColor: "transparent" }}
                        >
                            <Group justify="start" gap={12} className={classes.gray_color_text}>
                                <img src={google_ad_manager} alt="Preview Icon" width={20} height={20} />
                                <Text>Google Ad Manager</Text>
                                <Tooltip
                                    multiline
                                    width={240}
                                    label={i18n.t('publish.gamTooltip')}
                                    withArrow
                                    style={{ fontSize: 11, maxWidth: 220, lineHeight: 1.2 }}
                                >
                                    <img src={help_circle} alt="Help Icon" width={16} height={16} />
                                </Tooltip>
                            </Group>
                        </Accordion.Control>

                        <Accordion.Panel style={{ padding: 0 }} styles={{ content: { padding: 0 } }}>
                            <Paper
                                withBorder
                                radius="md"
                                style={{
                                    position: "relative",
                                    backgroundColor: "#1e1e1e",
                                    color: "#d4d4d4",
                                    padding: 11,
                                    height: 130,
                                    overflowY: "auto",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                }}
                            >
                                <CopyButton value={query?.data?.ad_script_clickmacro}>
                                    {({ copied, copy }) => (
                                        <ActionIcon
                                            onClick={copy}
                                            style={{ position: "absolute", top: 8, right: 8, color: "#ccc", zIndex: 2 }}
                                            variant="subtle"
                                        >
                                            <IconCopy size={16} />
                                        </ActionIcon>
                                    )}
                                </CopyButton>

                                <Textarea
                                    value={query?.data?.ad_script_clickmacro}
                                    readOnly
                                    variant="unstyled"
                                    styles={{
                                        root: { width: "100%", height: "100%" },
                                        wrapper: { width: "100%", height: "100%" },
                                        input: {
                                            backgroundColor: "transparent",
                                            color: "#d4d4d4",
                                            fontFamily: "monospace",
                                            height: "100%",
                                            paddingRight: 48,
                                            resize: "none",
                                            overflow: "scroll",
                                            scrollbarWidth: "none",
                                        },
                                    }}
                                />

                                <Anchor
                                    href="https://smartico.notion.site/How-to-run-your-Ad-in-Google-Ad-Manager-1d7141011e7780e2afdaf663ace9682c?pvs=4"
                                    target="_blank"
                                    size="xs"
                                    underline="always"
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        bottom: 10,
                                        color: "#24292E",
                                        backgroundColor: "white",
                                        padding: 6,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        borderRadius: 4,
                                        zIndex: 2,
                                    }}
                                >
                                    {i18n.t('publish.howToUseScript')}
                                    <IconExternalLink size={14} style={{ marginLeft: 4 }} />
                                </Anchor>
                            </Paper>
                        </Accordion.Panel>
                    </Accordion.Item>

                    {/* DV360 */}
                    <Accordion.Item value="gdv" style={{ backgroundColor: "transparent" }}>
                        <Accordion.Control
                            styles={{ control: { padding: 0 }, label: { overflow: "visible" } }}
                            style={{ padding: 0, backgroundColor: "transparent" }}
                        >
                            <Group justify="start" gap={12}>
                                <img src={google_display} alt="Preview Icon" width={20} height={20} />
                                <Text className={classes.gray_color_text}>Google Display & Video 360</Text>
                                <Tooltip
                                    multiline
                                    width={240}
                                    label={i18n.t('publish.dv360Tooltip')}
                                    withArrow
                                    style={{ fontSize: 11, maxWidth: 220, lineHeight: 1.2 }}
                                >
                                    <img src={help_circle} alt="Help Icon" width={16} height={16} />
                                </Tooltip>
                            </Group>
                        </Accordion.Control>

                        <Accordion.Panel style={{ padding: 0 }} styles={{ content: { padding: 0 } }}>
                            <Paper
                                withBorder
                                radius="md"
                                style={{
                                    position: "relative",
                                    backgroundColor: "#1e1e1e",
                                    color: "#d4d4d4",
                                    padding: 11,
                                    height: 130,
                                    overflowY: "auto",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                }}
                            >
                                <CopyButton value={query?.data?.ad_script_dv360}>
                                    {({ copied, copy }) => (
                                        <ActionIcon
                                            onClick={copy}
                                            style={{ position: "absolute", top: 8, right: 8, color: "#ccc", zIndex: 2 }}
                                            variant="subtle"
                                        >
                                            <IconCopy size={16} />
                                        </ActionIcon>
                                    )}
                                </CopyButton>

                                <Textarea
                                    value={query?.data?.ad_script_dv360}
                                    readOnly
                                    variant="unstyled"
                                    styles={{
                                        root: { width: "100%", height: "100%" },
                                        wrapper: { width: "100%", height: "100%" },
                                        input: {
                                            backgroundColor: "transparent",
                                            color: "#d4d4d4",
                                            fontFamily: "monospace",
                                            height: "100%",
                                            paddingRight: 48,
                                            resize: "none",
                                            overflow: "scroll",
                                            scrollbarWidth: "none",
                                        },
                                    }}
                                />

                                <Anchor
                                    href="https://smartico.notion.site/How-to-run-your-Ad-in-Google-Display-Video-360-1d7141011e7780538f36ed048787a658?pvs=4"
                                    target="_blank"
                                    size="xs"
                                    underline="always"
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        bottom: 10,
                                        color: "#24292E",
                                        backgroundColor: "white",
                                        padding: 6,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        borderRadius: 4,
                                        zIndex: 2,
                                    }}
                                >
                                    {i18n.t('publish.howToUseScript')}
                                    <IconExternalLink size={14} style={{ marginLeft: 4 }} />
                                </Anchor>
                            </Paper>
                        </Accordion.Panel>
                    </Accordion.Item>

                    {/* Other ad servers (no click macro) */}
                    <Accordion.Item value="other" style={{ backgroundColor: "transparent" }}>
                        <Accordion.Control
                            styles={{ control: { padding: 0 }, label: { overflow: "visible" } }}
                            style={{ padding: 0, backgroundColor: "transparent" }}
                        >
                            <Group justify="start" gap={12}>
                                <img src={other_ad} alt="Preview Icon" width={20} height={20} />
                                <Text className={classes.gray_color_text}>{i18n.t('publish.otherAdServer')}</Text>
                                <Tooltip
                                    multiline
                                    width={240}
                                    label={i18n.t('publish.otherTooltip')}
                                    withArrow
                                    style={{ fontSize: 11, maxWidth: 240, lineHeight: 1.2 }}
                                >
                                    <img src={help_circle} alt="Help Icon" width={16} height={16} />
                                </Tooltip>
                            </Group>
                        </Accordion.Control>

                        <Accordion.Panel style={{ padding: 0 }} styles={{ content: { padding: 0 } }}>
                            <Paper
                                withBorder
                                radius="md"
                                style={{
                                    position: "relative",
                                    backgroundColor: "#1e1e1e",
                                    color: "#d4d4d4",
                                    padding: 11,
                                    height: 130,
                                    overflowY: "auto",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                }}
                            >
                                <CopyButton value={query?.data?.ad_script}>
                                    {({ copied, copy }) => (
                                        <ActionIcon
                                            onClick={copy}
                                            style={{ position: "absolute", top: 8, right: 8, color: "#ccc", zIndex: 2 }}
                                            variant="subtle"
                                        >
                                            <IconCopy size={16} />
                                        </ActionIcon>
                                    )}
                                </CopyButton>

                                <Textarea
                                    value={query?.data?.ad_script}
                                    readOnly
                                    variant="unstyled"
                                    styles={{
                                        root: { width: "100%", height: "100%" },
                                        wrapper: { width: "100%", height: "100%" },
                                        input: {
                                            backgroundColor: "transparent",
                                            color: "#d4d4d4",
                                            fontFamily: "monospace",
                                            height: "100%",
                                            paddingRight: 48,
                                            resize: "none",
                                            overflow: "scroll",
                                            scrollbarWidth: "none",
                                        },
                                    }}
                                />

                                <Anchor
                                    href="https://smartico.notion.site/How-to-run-your-Ad-in-Google-Display-Video-360-1d7141011e7780538f36ed048787a658?pvs=4"
                                    target="_blank"
                                    size="xs"
                                    underline="always"
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        bottom: 10,
                                        color: "#24292E",
                                        backgroundColor: "white",
                                        padding: 6,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        borderRadius: 4,
                                        zIndex: 2,
                                    }}
                                >
                                    {i18n.t('publish.howToUseScript')}
                                    <IconExternalLink size={14} style={{ marginLeft: 4 }} />
                                </Anchor>
                            </Paper>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>

                {
                    withFooter && (
                        <>
                            <Divider size={1} color="#EDEDED" my={20} mx={-24} />

                            <Group justify="end">
                                <Button variant="default" component={Link} href={route("studio.home")} onClick={modals.closeAll}>
                                    {i18n.t('publish.backToInspiration')}
                                </Button>
                                <Button component={Link} href={route("studio.ad_builder.index")} onClick={modals.closeAll} color="pink">
                                    {i18n.t('publish.goToMyAds')}
                                </Button>
                            </Group>
                        </>
                    )
                }
                
            </Stack>
        ),
    })
}